import { P, stripHtmlTags } from '@piccolohealth/util';
import React from 'react';
import { ChakraV3 } from '../..';
import { clearHighlightMatches, highlightMatches } from '../../utils/highlight';
import { CommandInput } from './CommandInput';
import type { ActionsPage, CommandMenuContext } from './CommandMenu';

interface Props<A> {
  page: ActionsPage<A>;
  context: CommandMenuContext<A>;
}

export const CommandActionsPageContent = <A,>(props: Props<A>) => {
  const { page, context } = props;

  const [value, setValue] = React.useState<string[]>([]);
  const filterTextRef = React.useRef('');
  const contentRef = React.useRef<HTMLDivElement>(null);

  const initialItems = React.useMemo(() => {
    return page.actions.map((action) => {
      return {
        label: stripHtmlTags(action.rawTitle),
        renderedLabel: action.title,
        value: action.id,
        // We use AAA as a default, because it will pin them to the top
        category: action.category ?? 'AAA',
      };
    });
  }, [page]);

  const { contains } = ChakraV3.useFilter({ sensitivity: 'base' });

  const { collection, filter } = ChakraV3.useListCollection({
    initialItems,
    filter: contains,
    groupBy: (item) => item.category,
    groupSort: 'asc',
  });

  // When filterText is provided externally (e.g. from tiptap slash menu), drive filtering from it.
  React.useLayoutEffect(() => {
    if (context.input.kind === 'external') {
      filterTextRef.current = context.input.filterText;
      filter(context.input.filterText);
    }
  }, [context.input, filter]);

  // Highlight matching text in the rendered list items after each render
  React.useEffect(() => {
    highlightMatches(contentRef.current, filterTextRef.current);

    return () => {
      clearHighlightMatches();
    };
  });

  const onValueChange = (details: {
    value: string[];
    items: {
      label: string;
      value: string;
      category: string;
    }[];
  }) => {
    setValue(details.value);
    filterTextRef.current = '';
    filter('');
  };

  const onSelect = (details: { value: string }) => {
    const action = page.actions.find((action) => action.id === details.value);

    switch (action?.kind) {
      case 'goto': {
        context.pushPage(action.id, action.args);
        break;
      }
      case 'void': {
        action.action(context.ctx);
        context.onExit();
        break;
      }
      default:
        return;
    }
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filterTextRef.current = e.currentTarget.value;
    filter(e.currentTarget.value);
  };

  return (
    <ChakraV3.Listbox.Root
      collection={collection}
      value={value}
      onValueChange={onValueChange}
      onSelect={onSelect}
      variant='plain'
      loopFocus
      gap={0}
    >
      {context.input.kind === 'external' ? (
        <ChakraV3.Listbox.Input autoHighlight tabIndex={-1} hidden data-command-menu-input />
      ) : (
        <>
          <ChakraV3.Listbox.Input asChild autoHighlight>
            <CommandInput
              onChange={onFilterChange}
              currentPagesLength={context.currentPagesLength}
              popPage={context.popPage}
              placeholder='Search'
              isSearchable
            />
          </ChakraV3.Listbox.Input>
          <ChakraV3.Separator />
        </>
      )}
      <ChakraV3.Listbox.Content ref={contentRef} maxH='xs' w='lg'>
        {collection.group().map(([group, actions]) => (
          <ChakraV3.Listbox.ItemGroup key={group} m={0}>
            {group !== 'AAA' && (
              <ChakraV3.Listbox.ItemGroupLabel>{group}</ChakraV3.Listbox.ItemGroupLabel>
            )}
            {actions.map((action) => (
              <ChakraV3.Listbox.Item key={action.value} item={action}>
                {action.renderedLabel(context)}
              </ChakraV3.Listbox.Item>
            ))}
          </ChakraV3.Listbox.ItemGroup>
        ))}

        {P.isEmpty(collection.items) && (
          <ChakraV3.Listbox.Empty>No results found</ChakraV3.Listbox.Empty>
        )}
      </ChakraV3.Listbox.Content>
    </ChakraV3.Listbox.Root>
  );
};
